import {
    Component,
    ElementRef,
    Input,
    OnInit,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import {
    animationFrameScheduler,
    fromEvent,
    fromEventPattern,
    merge,
} from 'rxjs';
import { auditTime, mapTo, tap } from 'rxjs/operators';

import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../take-until-destroy.decorator';

import { determineScrollbarWidth } from './determine-scroll-bar-width.function';

/* TODO move to uikit */
@Component({
    selector: 'cmps-uikit-virtual-scroll',
    template: `<div [style.height.px]="_totalHeight"></div>`,
    host: {
        class: 'cmps-cdk-d-block cmps-cdk-pos-relative cmps-cdk-overflow-x-hidden cmps-cdk-overflow-y-auto',
    },
})
@TakeUntilDestroy()
export class VirtualScrollComponent implements OnInit {
    @Input() templateRef: TemplateRef<any>;
    @Input() offsetTop: number;
    @Input() offsetRight: number;
    @Input() buffer: number;
    @Input() itemWidth: number;
    @Input() itemHeight: number;

    @Input()
    set items(value: any[]) {
        this._items = value;
        this._render(null);
    }

    get items(): any[] {
        return this._items;
    }

    @Input() measureRef: HTMLElement;

    /** @private */
    _totalHeight: number;

    private _items: any[];
    private _itemsVisible: any[];
    private _container: HTMLElement;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _vcr: ViewContainerRef,
    ) {
        this.buffer = 10;
    }

    ngOnInit(): void {
        this._bindScroll();
    }

    private _bindScroll(): void {
        let ro: ResizeObserver;
        merge(
            fromEventPattern(
                (handler) => {
                    ro = new ResizeObserver(handler);
                    ro.observe(document.body);
                },
                () => {
                    ro.disconnect();
                },
            ).pipe(mapTo('resize')),
            fromEvent(this._el.nativeElement.parentElement, 'scroll').pipe(
                mapTo('scroll'),
            ),
        )
            .pipe(
                auditTime(150, animationFrameScheduler),
                tap((which) => {
                    this._render(which);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _render(which: string | null): void {
        this._vcr.clear();

        if (this._items == null) {
            return;
        }

        this._container = this._el.nativeElement.parentElement;

        if (which == null || which === 'resize') {
            if (this.itemWidth != null) {
                this._container.style.width = '1px';
            }

            this._container.style.height = '1px';
        }

        const offsetTop = this.offsetTop ?? 0;
        const offsetRight = this.offsetRight ?? 0;

        this._totalHeight = offsetTop + this.itemHeight * this._items.length;

        const viewport =
            this.measureRef?.getBoundingClientRect() ??
            this._container.getBoundingClientRect();
        const scrollTop = this._container.scrollTop;
        const startIdx = Math.max(
            0,
            Math.floor(scrollTop / this.itemHeight) - this.buffer,
        );
        const endIdx = Math.min(
            this._items.length,
            Math.ceil((scrollTop + viewport.height) / this.itemHeight) +
                this.buffer,
        );

        this._itemsVisible = this._items.slice(startIdx, endIdx);

        const view = this._vcr.createEmbeddedView(this.templateRef, {
            items: this._itemsVisible,
        });

        const rootNode = view.rootNodes[0] as HTMLElement;
        rootNode.style.position = 'absolute';
        rootNode.style.top = `${offsetTop + this.itemHeight * startIdx}px`;
        rootNode.style.right = `${offsetRight}px`;
        rootNode.style.left = '0';

        if (which == null || which === 'resize') {
            this._resize(viewport);
        }
    }

    private _resize(viewport: DOMRect): void {
        if (this.itemWidth != null) {
            let paddedWidth = 0;
            const verticalScrollBarVisible =
                this._itemsVisible.length < this._items.length;
            if (verticalScrollBarVisible) {
                paddedWidth = determineScrollbarWidth(this._container) ?? 0;
            }
            let width = this.itemWidth + paddedWidth;
            width = Math.min(width, viewport.width);
            this._container.style.width = `${width}px`;
        }

        let paddedHeight = 0;
        const horizontalScrollBarVisible =
            this.itemWidth != null && this.itemWidth > viewport.width;
        if (horizontalScrollBarVisible) {
            paddedHeight = determineScrollbarWidth(this._container) ?? 0;
        }
        let height = this._itemsVisible.length * this.itemHeight + paddedHeight;
        height = Math.min(height, viewport.height);
        this._container.style.height = `${height}px`;
    }
}
